//  ----------      START   IMPORT  BLOCK       ----------
import { useState } from "react";
import BaseLayout from "../../General/LandingPage/BaseLayout/BaseLayout";
//  Widgets
import { Container, ProgressBar, Card, Button, Badge } from "react-bootstrap";
import StepWelcome from "./Steps/StepWelcome";
import StepBasics from "./Steps/StepBasics";
import StepContact from "./Steps/StepContact";
import StepSubstanceUse from "./Steps/StepSubstanceUse";
import StepGeneralHealth from "./Steps/StepGeneralHealth";
//  Utils
import UserProfileOnboardingService from "../../../../API/Services/UserProfileOnboardingService";
import { useNavigate } from "react-router-dom";
//  Context
import { useLanguage } from "../../../../API/Context/LanguageContext";
import { useUser } from "../../../../API/Context/UserContext";
//  Config
import { VocabProfileOnboarding as Vocab } from "../../../../ConfigFiles/multilingual/VocabProfileOnboarding";
import { VocabButtons } from "../../../../ConfigFiles/multilingual/VocabButtons";
import { FRONTEND_ROUTES } from "../../../../ConfigFiles/Routes/frontend_routes";
import { OnboardingValidator } from "./OnboardingValidator";
import { DietCategoriesEnum } from "../../../../Types/Enums/DietCategoriesEnum";
//  ----------      END   IMPORT  BLOCK       ----------


const ProfileOnboarding = () => {
    const { lan } = useLanguage();
    const totalSteps = 4;
    const [step, setStep] = useState(0);
    const { setProfile } = useUser();

    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        birthday: "", 
        blood_type: "", 
        sex: "", 
        height: 170, 
        weight: 70, 
        marital_status: "", 
        scholarship: "", 
        religion: "", 
        occupation: "", 
        birthplace: "",
        phone_numbers: [""], 
        current_address: {
            street: "",
            number: "",
            interior_number: 0,
            city: "",
            state: "",             
            zip_code: "",
            country: "",
            google_maps_url: ""
        },
        emergency_contacts: [{ name: "", phone: "", relationship: "" }],
        diet: { 
            meals_per_day: 3, 
            water_liters: "",
            frequency_junk_food: "", 
            is_special_diet: false, 
            diet_type: DietCategoriesEnum.NONE
        },
        fitness: { 
            level: "", 
            frequency_per_week: "" 
        },
        substances: { 
            has_history: false, 
            substances_detail: []
        },
        sexual_health: { 
            status: false, 
            partners_range: "", 
            protection: "" 
        }
    });

    const currentStepIsValid = OnboardingValidator[step] ? OnboardingValidator[step](formData) : false;
 
    const updateFormData = (newData) => setFormData(prev => ({ ...prev, ...newData }));

    const handleFinalSubmit = async () => {
  setIsSubmitting(true);
  try {
    const response = await UserProfileOnboardingService.createProfileOnboarding(formData);
    
    console.log("🚀 Onboarding created successfully:", response);

    // 1. Update user context (handle direct object or response.data)
    const updatedProfile = response?.data || response;
    setProfile(updatedProfile);

    // 2. Navigate regardless of exact JSON body keys
    navigate(FRONTEND_ROUTES.onboarding_success, { 
      state: { flow: "PURE_PATIENT" },
      replace: true
    });

  } catch (error) {
    alert("Submission failed. Please check your connection.");
    console.error("Submission Error:", error);
  } finally {
    setIsSubmitting(false);
  }
};

    const handleNext = () => {
        //  Only increment if we aren't at the last index
        if (step < totalSteps) {
            setStep(s => s + 1);
        } else {
            // This is reached when step === 4
            handleFinalSubmit();
        }
    };


    return (
        <BaseLayout>
            <Container className="py-5 onboarding-container">
                {/* --- HEADER SECTION --- */}
                <div className="text-center mb-4">
                    <h2 className="fw-bold text-primary">{Vocab.onboarding_title[lan]}</h2>
                    <p className="text-muted mb-2">{Vocab.onboarding_message[lan]}</p>
                    
                    {/* Progress with Step Indicator */}
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <ProgressBar 
                            now={(step / totalSteps) * 100} 
                            style={{ height: "10px", width: "100%", maxWidth: "400px" }} 
                            className="rounded-pill shadow-sm"
                        />
                        <Badge bg="primary" className="rounded-pill px-3 py-2">
                            {step} / {totalSteps}
                        </Badge>
                    </div>

                    {/* Confidentiality Block */}
                    <div className="mt-3 p-2 bg-light rounded d-inline-block border shadow-sm">
                        <small className="text-muted">
                            {Vocab.confidentiality_legend[lan]}{" "}
                            <a href={FRONTEND_ROUTES.home_privacy} target="_blank" className="text-decoration-none fw-bold">
                                {Vocab.confidentiality_link[lan]}
                            </a>
                        </small>
                    </div>
                </div>

                <Card className="shadow-lg border-0 rounded-4">
                    <Card.Body className="p-4 p-md-5">
                        {step === 0 && <StepWelcome/>}
                        {step === 1 && <StepBasics data={formData} update={updateFormData}/>}
                        {step === 2 && <StepContact data={formData} update={updateFormData}/>}
                        {step === 3 && <StepSubstanceUse data={formData} update={updateFormData}/>}
                        {step === 4 && <StepGeneralHealth data={formData} update={updateFormData}/>}

                        <div className="d-flex justify-content-between mt-4">
        <Button 
          variant="secondary" 
          onClick={() => setStep(s => s - 1)} 
          disabled={step === 0}
        >
          {VocabButtons.back[lan]}
        </Button>

        <Button 
          variant={step === totalSteps ? "success" : "primary"} 
          disabled={!currentStepIsValid} 
          onClick={handleNext}
        >
          {step === totalSteps ? VocabButtons.send[lan] : VocabButtons.next[lan]}
        </Button>
      </div>
                    </Card.Body>
                </Card>
            </Container>
        </BaseLayout>
    );
};

export default ProfileOnboarding;